'use client'

import React, { useState, forwardRef, useImperativeHandle, useRef, useEffect } from 'react'
import { useSearchParams } from 'next/navigation'
import { CopilotWithOutAnimation } from '@acko-swat-sdk/ui'
import { generateLoginUrl } from '@utils/loginUtil'
import { COPILOT_URL } from './AssistBot.constants'
import '@acko-swat-sdk/ui/dist/index.css'
import styles from './AssistBot.module.scss'

declare global {
  interface Window {
    copilot: {
      open: () => void
      close: () => void
      setContext: (context: unknown) => void
      sendMessage: (message: string) => void
      getSummary: () => string
    }
  }
}

interface AssistBotProps {
  hideFAB?: boolean
  envConfig?: object
}

const AssistBot = forwardRef(({ hideFAB = false, envConfig }: AssistBotProps, ref) => {
  const [loaded, setLoaded] = useState(false)
  const searchParams = useSearchParams()
  const assistBotRef = useRef<HTMLDivElement>(null)

  useImperativeHandle(ref, () => ({
    openCopilot: () => {
      if (!loaded) {
        console.warn('Copilot is not loaded yet.')
        return
      }
      window?.copilot?.open()
    },
    closeCopilot: () => {
      if (!loaded) {
        console.warn('Copilot is not loaded yet.')
        return
      }
      window?.copilot?.close()
    },
    setCopilotContext: (context: unknown) => {
      if (!loaded) {
        console.warn('Copilot is not loaded yet.')
        return
      }
      window?.copilot?.setContext(context)
    },
    sendCopilotMessage: (message: string) => {
      if (!loaded) {
        console.warn('Copilot is not loaded yet.')
        return
      }
      window?.copilot?.sendMessage(message)
      window?.copilot?.open()
    },
    getCopilotSummary: () => window?.copilot?.getSummary(),
  }))

  // Open copilot post login redirect
  useEffect(() => {
    const copilotOpen = searchParams?.get('copilotOpen') === 'true'
    if (copilotOpen && loaded) {
      setTimeout(() => {
        window?.copilot?.open()
        window?.copilot?.sendMessage('I have logged in, please continue')
        console.log('I have logged in, please continue')
        const url = new URL(window.location.href)
        url.searchParams.delete('copilotOpen')
        window.history.replaceState({}, '', url.toString())
      }, 3000)
    }
  }, [searchParams, loaded])

  const redirectToLogin = () => {
    const redirectionUrl = encodeURIComponent('/contact-us?copilotOpen=true')
    const loginUrlWithState = new URL(generateLoginUrl(envConfig))
    loginUrlWithState.searchParams.set('state', redirectionUrl)

    window.location.href = loginUrlWithState.toString()
  }

  const handleCopilotEvent = (event: string, _payload: unknown) => {
    switch (event) {
      case 'connection-initialised-completely':
        break
      case 'join-conversation':
        setLoaded(true)
        break
      case 'message':
        break
      case 'closeCopilot':
        break
      case 'openCopilot':
        break
      case 'summary':
        break
      case 'login':
        redirectToLogin()
        break
    }
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (assistBotRef.current && !assistBotRef.current.contains(event.target as Node)) {
        window?.copilot?.close()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div className={styles.assistBotContainer} ref={assistBotRef}>
      <CopilotWithOutAnimation
        url={COPILOT_URL}
        fabHide={hideFAB}
        onEvent={handleCopilotEvent}
        right={40}
        bottom={40}
        zIndex={500}
      />
    </div>
  )
})

export default AssistBot
